import { OpenFeature } from '@openfeature/react-sdk'
import stringify from 'fast-safe-stringify'
import { z } from 'zod'

import { BOOLEAN_FEATURE_FLAGS, STRING_FEATURE_FLAGS } from './hooks/useFlag'
import { getEntries } from './utils/object'
import { InMemoryFlagProvider } from './utils/openFeature/InMemoryFlagProvider'

function toBoolean(value: unknown): boolean {
    return value === true || String(value).trim() === 'true'
}

function bool(defaultValue: boolean) {
    return z
        .union([z.literal('true'), z.literal('false'), z.boolean()])
        .default(defaultValue)
        .transform(toBoolean)
}

type FlagSchema = z.ZodOptional<z.ZodBoolean> | z.ZodOptional<z.ZodString>

const flagProviderZodObject = z.object(
    Object.fromEntries<FlagSchema>([
        ...Object.keys(BOOLEAN_FEATURE_FLAGS).map(key => [key, z.boolean().optional()] as const),
        ...Object.keys(STRING_FEATURE_FLAGS).map(key => [key, z.string().optional()] as const),
    ])
)

function parseJsonString(s: unknown): unknown {
    return typeof s === 'string' && s !== '' ? JSON.parse(s.replaceAll("'", '"')) : s
}
// Note: Don't use .coerce.boolean() because it will coerce 'false' to true
// Handle all default values here, rather than where the env is used!
// Please keep this list sorted alphabetically
const envSchema = z.object({
    VITE_ADMIN_PANEL_PROTECTED: bool(true),
    VITE_API_BASE_URL: z.string(),
    VITE_API_OAUTH_CLIENT: z.string(),
    VITE_BETTERSTACK_STATUS_PAGE_URL: z.string().optional(),
    VITE_BOOKING_SHOULD_FAIL: bool(false), // for controlled failure in E2E
    VITE_BOOKING_SKIP_DELAY: bool(false), // for controlled skipping of mock-dips delay
    VITE_CLIENT_BASE_URL: z.string(),
    VITE_DIPS_BASE_URL: z.string(),
    VITE_DIPS_BEHIND_IAP: bool(false), // Google IAP fronts VITE_DIPS_BASE_URL (mock-dips on GKE)
    VITE_DIPS_CLIENT_ID: z.string(),
    VITE_DIPS_TREATMENT_LOCATION_ID: z.string(),
    VITE_FLAG_PROVIDER: z.preprocess(parseJsonString, flagProviderZodObject).optional(), // undefined means flagsmith
    VITE_ENABLE_OTEL: bool(false),
    VITE_ENABLE_SENTRY: bool(true),
    VITE_ENABLE_SENTRY_TRACE_PROPAGATION_TO_API: bool(true),
    VITE_ENABLE_SENTRY_TRACE_PROPAGATION_TO_DIPS: bool(false),
    VITE_ENABLE_SENTRY_USER_FEEDBACK: bool(false),
    VITE_FLAGSMITH_CLIENT_SIDE_ENVIRONMENT_KEY: z.string().optional(),
    VITE_FLAGSMITH_API_URL: z.string().optional(),
    VITE_FF_REFRESH_TOKEN: bool(true),
    VITE_GIT_SHA: z.string().optional(),
    VITE_GIT_TAG: z.string().optional(),
    VITE_NO_UNSTABLE_EFFECTS: bool(false), // disable source of flakiness in e2e tests
    VITE_PKCE_AUTHORIZE_URL: z.string().optional().default(''),
    VITE_PKCE_CLIENT_ID: z.string().optional().default(''),
    VITE_PKCE_FOR_DEVS: bool(false), // if false, PKCE flow is skipped for devs
    VITE_PKCE_LOGOUT_URL: z.string().optional().default(''),
    VITE_PKCE_REDIRECT_URL: z.string().optional().default(''),
    VITE_PKCE_SCOPES: z.string().optional().default(''),
    VITE_PKCE_TOKEN_URL: z.string().optional().default(''),
    VITE_PUBLIC_DIR: z.string().optional(),
    VITE_REFETCH_INTERVAL_S: z.coerce.number().default(30), // 0 disables refetching
    VITE_REMOVE_CONFIRMED_UNSCHEDULED_SURGERY_DELAY_S: z.coerce.number().default(5 * 60 /* 5 minutes */),
    VITE_SEGMENT_HOSTNAME: z.string().optional(),
    VITE_SEGMENT_TENANT_NAME: z.string().optional(), // Remove this when all tenants are migrated to VITE_TENANT_NAME
    VITE_SEGMENT_WRITE_KEY: z.string().optional(),
    VITE_SENTRY_DSN: z.string().optional(),
    VITE_SENTRY_ENVIRONMENT: z.string().optional(),
    VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE: z.coerce.number().optional(),
    VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE: z.coerce.number().optional(),
    VITE_SENTRY_TRACES_SAMPLE_RATE: z.coerce.number().optional(),
    VITE_STRICT_MODE: bool(true),
    VITE_TENANT_NAME: z.string().optional(),
    VITE_TODAY: z.string().optional(),
    VITE_WAITINGLIST_HIDE_ASA_FOR_DEPARTMENT_IDS: z.string().optional(),
    VITE_HINT_WEBSOCKET: bool(true),
    VITE_OTEL_TRACE_ENDPOINT: z.string().optional(),
    VITE_OTEL_METRICS_ENDPOINT: z.string().optional(),
    VITE_OTEL_SERVICE_NAMESPACE: z.string().optional(),
    VITE_OTEL_BSP_MAX_EXPORT_BATCH_SIZE: z.coerce.number().optional(),
    VITE_OTEL_BSP_MAX_QUEUE_SIZE: z.coerce.number().optional(),
    VITE_OTEL_BSP_SCHEDULE_DELAY: z.coerce.number().optional(),
    VITE_OTEL_BSP_EXPORT_TIMEOUT: z.coerce.number().optional(),
})

// eslint-disable-next-line local-rules/disallow-import-meta-env
const _originalEnv = envSchema.parse(import.meta.env)

type Env = z.infer<typeof envSchema>
export type EnvKey = keyof Env

// Zod applies a field's default even when `.partial()` makes the field optional.
// Keep only the keys that the input sets, so a default never overrides the real env.
function parseOverrides(schema: z.ZodType<Partial<Env>>, input: unknown): Partial<Env> {
    const parsed = schema.parse(input)
    const isSet = (key: string) => typeof input === 'object' && input !== null && key in input
    return Object.fromEntries(Object.entries(parsed).filter(([key]) => isSet(key)))
}

function retrieveEnv() {
    const storedEnvString = localStorage.getItem('env-override')

    if (storedEnvString) {
        try {
            return parseOverrides(envSchema.partial().strict(), JSON.parse(storedEnvString))
        } catch (error) {
            console.error('Failed to parse stored env, removing storage:', error)
            localStorage.removeItem('env-override')
        }
    }

    return {}
}

function getCurrentEnv() {
    return { ..._originalEnv, ...retrieveEnv() }
}

export function getEnvVarDiff(compareToEnv: Partial<Env> = getCurrentEnv()) {
    return Object.fromEntries(getEntries(compareToEnv).filter(([key, value]) => _originalEnv[key] !== value))
}

export function storeEnv(envChange: Partial<Env>) {
    const envChangeParsed = parseOverrides(envSchema.partial(), envChange)
    const newEnv = { ...getCurrentEnv(), ...envChangeParsed }
    const storedEnv = getEnvVarDiff(newEnv)

    localStorage.setItem('env-override', stringify(storedEnv))
}

const env = getCurrentEnv()

if (typeof env.VITE_FLAG_PROVIDER === 'object') {
    OpenFeature.setProvider(new InMemoryFlagProvider(env.VITE_FLAG_PROVIDER))
}

export default env
